/**
 * UC1 — Priority areas (client spec `2026-09-29-UC1_LAUNCH_SPEC.md`, design §3): the config
 * note, the level switch (watershed|admin0) and the constraint chips sit above a four-tab
 * strip (`#uc1Tabs`) — Overview, Key indicators, Why these areas, Methodology. Only the
 * active sub-tab's panel is mounted. There is no unit pick: the selection is a map click
 * (`useUnitLink`).
 *
 * The Overview follows the active chip (2 Oct chart round): Priority keeps the programme
 * headlines, a constraint answers for itself in `#uc1ChipOverview`, and `#uc1UnitsChart`
 * under either of them is the matrix or that constraint's per-unit bars.
 *
 * Client round 3 Oct 2026: `#intelFocus` under the level switch is a second way in. It
 * does exactly what a map click does (`useUnitLink.pick`) and additionally fits the map,
 * so every panel, chip outline and card follows from the same two store fields; with a
 * unit focused the Overview tiles are that unit's own, with a "Programme" link back.
 *
 * The launch round removes the constraint-overlap checkbox (`#intelOverlay`) and the
 * `intel_uc1_overlap` raster it switched on: the definition has no `overlay` block any
 * more, and one constraint at a time on the choropleth is what replaced it.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChartFrame } from '../../components/charts/ChartFrame';
import { useAppStore, WATERSHED_LEVEL } from '../../lib/state';
import { ConstraintChips } from './ConstraintChips';
import { DimensionBars, type DimRow } from './charts/DimensionBars';
import { Headlines } from './Headlines';
import { IntelFocus } from './IntelFocus';
import { Uc1ChipOverview } from './Uc1ChipOverview';
import { Uc1FocusConstraints } from './Uc1FocusConstraints';
import { Uc1FocusTiles } from './Uc1FocusTiles';
import { Uc1UnitsChart } from './Uc1UnitsChart';
import { Methodology } from './Methodology';
import { SelectedArea } from './SelectedArea';
import { SubPanel, SubTabs } from './SubTabs';
import { Underlying } from './Underlying';
import { Why } from './Why';
import { IndicatorsUsed } from './IndicatorsUsed';
import { useIntelSummary, useIntelUnitDetail, useIntelUnits } from './api';
import { chipPaint, type CountryShare } from './uc1Paint';
import { CONTEXT_UNITS, normaliseChip, type Uc1Level } from './uc1Constants';
import { useUnitLink } from './useUnitLink';

const UC1_TABS = ['overview', 'indicators', 'why', 'method'] as const;
type Uc1Tab = (typeof UC1_TABS)[number];

export function Uc1() {
  const { t } = useTranslation();
  const [level, setLevel] = useState<Uc1Level>('watershed');
  const addResultLayer = useAppStore((s) => s.addResultLayer);
  const setReportingLevel = useAppStore((s) => s.setReportingLevel);
  const storedChip = useAppStore((s) => s.intel.uc1Chip);
  const chip = normaliseChip(storedChip);
  const storedSubTab = useAppStore((s) => s.intel.subTab);
  const subTab: Uc1Tab = UC1_TABS.includes(storedSubTab as Uc1Tab)
    ? (storedSubTab as Uc1Tab)
    : UC1_TABS[0];

  const summary = useIntelSummary('uc1');
  const units = useIntelUnits('uc1', level);
  const { unit, pick, clear } = useUnitLink('uc1');
  // Why these areas reads the watershed rows whatever the level switch says (NOTES #729).
  const watershedRows = useIntelUnits('uc1', 'watershed');
  const detail = useIntelUnitDetail('uc1', unit);

  const rows = useMemo(() => units.data ?? [], [units.data]);
  /**
   * The focused unit's own row at the level the pane is on. A unit focused at the other
   * level (a watershed while the switch is on Country) has none here, and the Overview
   * then stays on the programme headlines rather than printing a half-empty unit block.
   */
  const focusedRow = rows.find((r) => r.unit_id === unit);

  /**
   * Country + constraint chip: the share of that country's watersheds flagged. It rides
   * on the country units row itself (`metrics[dim].value`, in percent) since the 2 Oct
   * round — the pane used to fan out one `uc1/units/{id}` detail per country for the same
   * number, which was four requests for a figure the rows already carry.
   */
  const shareByUnit = useMemo(() => {
    const out = new Map<string, CountryShare>();
    if (level !== 'admin0' || chip === 'priority') return out;
    for (const row of rows) {
      const pct = row.metrics?.[chip]?.value;
      out.set(row.unit_id, {
        share: typeof pct === 'number' ? pct / 100 : null,
        reason: row.reasons?.[chip] ?? null,
      });
    }
    return out;
  }, [rows, level, chip]);

  const paint = useMemo(
    () => chipPaint({ rows, chip, level, shareByUnit, label: (k, o) => t(k, o) }),
    [rows, chip, level, shareByUnit, t],
  );

  /**
   * The pane's own level switch decides which polygons UC1 has scores for; the map's
   * operational hierarchy decides which polygons a `units` result layer is painted from
   * (`features/map/resultUnits.ts` — the explorer's own geometry, so a result outline can
   * never sit a pixel off the boundary under it). Those two have to agree or the
   * choropleth matches no feature and the map stays blank while the legend, the chips and
   * the scorecard all fill in — which is what the app's default level (admin 1) did to
   * every reader who opened UC1 without touching the hierarchy widget.
   *
   * So the pane states the level it needs: watershed on mount, country (admin 0) while the
   * switch is on `admin0`. Only UC1 does this — UC2 and UC3 paint artefacts and unit
   * highlights, not a full-coverage choropleth, and neither has a level switch to follow.
   */
  useEffect(() => {
    setReportingLevel(level === 'admin0' ? 0 : WATERSHED_LEVEL);
  }, [level, setReportingLevel]);

  /**
   * The choropleth is a `units` result layer, not a `choropleth` one: a `choropleth`
   * classifies continuous values through §G's ramp/method/k, which would smear three sharp
   * categories (or a yes/no/not-assessed) into a quantile ramp. A `units` layer takes the
   * explicit per-unit `colors` — and, since this round, `patterns` — that the fixed palette
   * needs. Redrawn in place (same id) whenever the level or the chip changes.
   */
  useEffect(() => {
    if (!rows.length) return;
    addResultLayer(
      {
        id: 'intel:uc1:choropleth',
        kind: 'units',
        label:
          chip === 'priority'
            ? t('intel.uc1.choroplethLabel')
            : t('intel.uc1.dimension.' + chip, { defaultValue: chip }),
        unit_ids: rows.map((r) => r.unit_id),
        colors: paint.colors,
        patterns: paint.patterns,
        details: paint.details,
        classes: paint.classes,
      },
      { jobId: `uc1-${level}-${chip}`, tool: 'uc1' },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, level, chip, paint]);

  // Methodology sub-tab: "Context (not scored)" (water-point density, tenure, the GAEZ
  // context metrics) — read straight off the fetched detail.
  const contextValues = (detail.data?.values as Record<string, unknown> | undefined)?.context as
    | Record<string, number | null>
    | undefined;
  const contextRows: DimRow[] = contextValues
    ? Object.keys(contextValues)
        .filter((id) => typeof contextValues[id] === 'number')
        .map((id) => ({
          id,
          label: t(`intel.uc1.context.${id}`, { defaultValue: id }),
          value: contextValues[id] as number,
          unit: CONTEXT_UNITS[id] ?? '',
        }))
    : [];

  return (
    <div className="intel-uc1">
      {summary.data?.stale ? (
        <div id="intelConfigNote" className="note">
          {t('intel.configNote')}
        </div>
      ) : null}

      {/* Layout round 2 Oct 2026: the level switch, the chips and the chip legend are one
          card — they are the one set of controls that says what the map is showing, and as
          three loose blocks they read as three unrelated rows above the tabs. */}
      <div className="intel-card intel-mapcontrols">
        <label className="intel-level">
          <span>{t('intel.uc1.levelLabel')}</span>
          <select
            id="intelLevel"
            value={level}
            onChange={(e) => setLevel(e.target.value as Uc1Level)}
          >
            <option value="watershed">{t('intel.uc1.levelWatershed')}</option>
            <option value="admin0">{t('intel.uc1.levelCountry')}</option>
          </select>
        </label>
        <IntelFocus
          rows={rows}
          level={level}
          selected={unit}
          classLabel={(row) => t(`intel.uc1.category.${row.category}`, { defaultValue: row.category })}
          onFocus={(id, name) => void pick(id, name)}
          onClear={clear}
        />
        <ConstraintChips level={level} reasons={paint.reasons} />
      </div>

      <SubTabs prefix="uc1" tabs={UC1_TABS} />

      {subTab === 'overview' ? (
        <SubPanel prefix="uc1" name="overview">
          {/* The Overview follows the active chip (2 Oct round): Priority keeps the
              programme headlines, a constraint answers for itself. The per-unit chart sits
              under whichever of the two is showing. */}
          {/* Client round 3 Oct 2026: a focused unit answers for itself on **every** chip,
              not only on Priority. A reader who clicked a watershed while Soil is active
              was being shown the five-constraint programme breakdown — the question they
              had just stopped asking. The tiles carry the chip, so on a constraint they
              say that unit's own flag and its measured value. */}
          {focusedRow ? (
            <>
              <Uc1FocusTiles
                row={focusedRow}
                detail={detail.data ?? null}
                name={focusedRow.name}
                chip={chip}
                onBack={clear}
              />
              {/* Client round 3 Oct 2026: on Priority, the combined chip, the focused unit's
                  five constraints are spelled out under the tiles — the same rows as the Key
                  indicators scorecard, from the same builder. A constraint chip is already a
                  one-constraint question, and its tiles answer it. */}
              {chip === 'priority' && detail.data?.unit.id === unit ? (
                <Uc1FocusConstraints detail={detail.data} />
              ) : null}
            </>
          ) : chip === 'priority' ? (
            summary.data ? <Headlines summary={summary.data} /> : null
          ) : (
            <Uc1ChipOverview rows={rows} chip={chip} />
          )}
          <Uc1UnitsChart rows={rows} chip={chip} level={level} selectedId={unit} />
        </SubPanel>
      ) : null}

      {subTab === 'indicators' ? (
        <SubPanel prefix="uc1" name="indicators">
          <IndicatorsUsed />
          {detail.data ? <SelectedArea detail={detail.data} /> : null}
        </SubPanel>
      ) : null}

      {subTab === 'why' ? (
        <SubPanel prefix="uc1" name="why">
          <Why rows={watershedRows.data ?? []} />
        </SubPanel>
      ) : null}

      {subTab === 'method' ? (
        <SubPanel prefix="uc1" name="method">
          <Methodology useCase="uc1" open />
          {detail.data ? <Underlying items={detail.data.underlying} /> : null}
          {contextRows.length ? (
            <div className="intel-context intel-card" data-testid="intel-context">
              <h6 className="intel-card-title">{t('intel.uc1.contextTitle')}</h6>
              <ChartFrame
                title={t('intel.uc1.contextTitle')}
                xLabel={t('intel.axis.againstReference')}
                yLabel={t('charts.axis.indicator')}
                testId="uc1-context-chart"
              >
                {() => (
                  <DimensionBars
                    rows={contextRows}
                    ariaLabel={t('intel.uc1.contextTitle')}
                    scale="row"
                    referenceLabel={t('intel.chart.reference')}
                    refCaption={t('intel.chart.ref')}
                  />
                )}
              </ChartFrame>
            </div>
          ) : null}
        </SubPanel>
      ) : null}
    </div>
  );
}

export default Uc1;
